import { createContext, useContext, useMemo, ReactNode } from "react";

import {
  useCostMetrics,
  CostMetricsResponse,
  MonthlyMetrics,
  calculateLevel,
  calculateLevelProgress,
  getNextLevelThreshold,
} from "./api";
import { getAchievements, Achievement } from "./constants";

interface MoneySavedContextValue {
  isLoading: boolean;
  error: Error | null;
  totalSavings: number;
  level: number;
  levelProgress: number;
  nextLevelThreshold: number;
  monthlySavings: MonthlyMetrics[];
  achievements: Achievement[];
  totalEstimatedSavings: number;
  totalSpend: number;
  totalConversions: number;
  totalPrivacyConversions: number;
  capiConnectedDate: string | null;
  baseline: CostMetricsResponse["baseline"];
}

const MoneySavedContext = createContext<MoneySavedContextValue | null>(null);

function getDateRange() {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - 12);

  return {
    startDate: startDate.toISOString().split("T")[0],
    endDate: endDate.toISOString().split("T")[0],
  };
}

export function MoneySavedProvider({ children }: { children: ReactNode }) {
  const { startDate, endDate } = useMemo(() => getDateRange(), []);

  const { data, isLoading, error } = useCostMetrics(startDate, endDate);

  const value = useMemo<MoneySavedContextValue>(() => {
    const totalSavings = data?.total_savings ?? 0;
    const level = calculateLevel(totalSavings);
    const levelProgress = calculateLevelProgress(totalSavings, level);
    const nextLevelThreshold = getNextLevelThreshold(level);
    const monthlySavings = data?.monthly ?? [];
    const achievements = getAchievements(totalSavings);

    const totalEstimatedSavings = monthlySavings.reduce(
      (sum, m) => sum + (m.estimated_savings ?? 0),
      0,
    );
    const totalSpend = monthlySavings.reduce(
      (sum, m) => sum + (m.spend ?? 0),
      0,
    );
    const totalConversions = monthlySavings.reduce(
      (sum, m) => sum + (m.conversions ?? 0),
      0,
    );
    const totalPrivacyConversions = monthlySavings.reduce(
      (sum, m) => sum + (m.privacy_conversions ?? 0),
      0,
    );

    return {
      isLoading,
      error: error as Error | null,
      totalSavings,
      level,
      levelProgress,
      nextLevelThreshold,
      monthlySavings,
      achievements,
      totalEstimatedSavings,
      totalSpend,
      totalConversions,
      totalPrivacyConversions,
      capiConnectedDate: data?.capi_connected_date ?? null,
      baseline: data?.baseline ?? null,
    };
  }, [data, isLoading, error]);

  return (
    <MoneySavedContext.Provider value={value}>
      {children}
    </MoneySavedContext.Provider>
  );
}

export function useMoneySaved() {
  const context = useContext(MoneySavedContext);
  if (!context) {
    throw new Error("useMoneySaved must be used within a MoneySavedProvider");
  }
  return context;
}
